<template>
    <div class="add-to-collection">
      <button @click="openModal" class="bookmark-btn">
        <font-awesome-icon :icon="['fas', 'bookmark']" />
        <span>{{ isBookmarked ? 'Tersimpan' : 'Simpan Buku' }}</span>
      </button>
      
      <!-- Modal for adding to collection -->
      <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ isBookmarked ? 'Buku Tersimpan' : 'Simpan Buku' }}</h3>
            <button @click="showModal = false" class="close-btn">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          
          <div v-if="loading" class="modal-loading">
            <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
            <p>Memuat koleksi...</p>
          </div>
          
          <div v-else-if="collections.length === 0" class="no-collections">
            <p>Anda belum memiliki koleksi.</p>
            <button @click="createFirstCollection" class="btn-primary">
              <font-awesome-icon :icon="['fas', 'plus']" /> Buat Koleksi Baru
            </button>
          </div>
          
          <div v-else>
            <div v-if="bookCollectionStatus.length > 0" class="already-saved">
              <p>Buku ini sudah disimpan di koleksi:</p>
              <ul class="collection-list">
                <li v-for="status in bookCollectionStatus" :key="status.collectionId">
                  <span class="collection-name">{{ status.collectionName }}</span>
                  <button @click="removeFromCollection(status.collectionId, status.bookmarkId)" class="remove-btn">
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </li>
              </ul>
            </div>
            
            <div v-if="availableCollections.length > 0">
              <p>Pilih koleksi untuk menyimpan:</p>
              <ul class="collection-list">
                <li v-for="collection in availableCollections" :key="collection.id">
                  <span class="collection-name">{{ collection.name }}</span>
                  <button @click="addToCollection(collection.id)" class="add-btn" :disabled="isAdding">
                    <font-awesome-icon v-if="isAdding && activeCollectionId === collection.id" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'plus']" />
                  </button>
                </li>
              </ul>
            </div>
            
            <div class="collection-actions">
              <button @click="showNewCollectionForm = true" class="btn-secondary" v-if="!showNewCollectionForm">
                <font-awesome-icon :icon="['fas', 'plus']" /> Buat Koleksi Baru
              </button>
            </div>
            
            <!-- Create new collection form -->
            <div v-if="showNewCollectionForm" class="new-collection-form">
              <h4>Buat Koleksi Baru</h4>
              <div class="form-group">
                <label for="newCollectionName">Nama Koleksi</label>
                <input 
                  type="text" 
                  id="newCollectionName" 
                  v-model="newCollection.name" 
                  placeholder="Nama koleksi" 
                  required
                />
              </div>
              <div class="form-group">
                <label for="newCollectionDesc">Deskripsi (Opsional)</label>
                <textarea 
                  id="newCollectionDesc" 
                  v-model="newCollection.description" 
                  placeholder="Deskripsi singkat koleksi"
                  rows="2"
                ></textarea>
              </div>
              <div class="form-actions">
                <button @click="showNewCollectionForm = false" class="btn-cancel">Batal</button>
                <button @click="createCollection" class="btn-primary" :disabled="isCreating">
                  <font-awesome-icon v-if="isCreating" :icon="['fas', 'spinner']" spin /> 
                  {{ isCreating ? 'Menyimpan...' : 'Simpan' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import Swal from 'sweetalert2';
  
  export default {
    name: 'AddToCollectionButton',
    props: {
      bookId: {
        type: [Number, String],
        required: true
      },
      bookSlug: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        showModal: false,
        loading: false,
        collections: [],
        bookCollectionStatus: [], // Array of {collectionId, collectionName, bookmarkId}
        showNewCollectionForm: false,
        newCollection: {
          name: '',
          description: '',
          is_public: false
        },
        isCreating: false,
        isAdding: false,
        activeCollectionId: null
      };
    },
    computed: {
      isBookmarked() {
        return this.bookCollectionStatus.length > 0;
      },
      availableCollections() {
        // Filter out collections that already contain this book
        const savedCollectionIds = this.bookCollectionStatus.map(status => status.collectionId);
        return this.collections.filter(collection => !savedCollectionIds.includes(collection.id));
      }
    },
    methods: {
      async openModal() {
        if (!localStorage.getItem('token')) {
          // Redirect to login if not logged in
          this.$router.push('/login');
          return;
        }
        
        this.showModal = true;
        await this.fetchCollections();
        await this.checkBookmarkStatus();
      },
      
      async fetchCollections() {
        this.loading = true;
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/collections/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          this.collections = response.data;
          
          // Create default collection if no collections exist
          if (this.collections.length === 0) {
            await this.createDefaultCollection();
          }
        } catch (error) {
          console.error('Error fetching collections:', error);
          this.showToast('error', 'Gagal memuat koleksi');
          
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            this.$router.push('/login');
          }
        } finally {
          this.loading = false;
        }
      },
      
      async createDefaultCollection() {
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/collections/default/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Add the default collection to the collections array
          this.collections = [response.data];
        } catch (error) {
          console.error('Error creating default collection:', error);
        }
      },
      
      async checkBookmarkStatus() {
        this.loading = true;
        this.bookCollectionStatus = [];
        
        try {
          for (const collection of this.collections) {
            const response = await axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/api/collections/${collection.id}/`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            // Check if this book exists in the collection
            const bookmark = response.data.bookmarks?.find(b => b.book == this.bookId);
            
            if (bookmark) {
              this.bookCollectionStatus.push({
                collectionId: collection.id,
                collectionName: collection.name,
                bookmarkId: bookmark.id
              });
            }
          }
        } catch (error) {
          console.error('Error checking bookmark status:', error);
        } finally {
          this.loading = false;
        }
      },
      
      async addToCollection(collectionId) {
        this.isAdding = true;
        this.activeCollectionId = collectionId;
        
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/api/collections/${collectionId}/add-book/`,
            {
              book_id: this.bookId,
              notes: ''
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // Add to bookCollectionStatus
          const collection = this.collections.find(c => c.id === collectionId);
          if (collection) {
            this.bookCollectionStatus.push({
              collectionId,
              collectionName: collection.name,
              bookmarkId: response.data.id
            });
          }
          
          this.showToast('success', 'Buku berhasil disimpan ke koleksi');
        } catch (error) {
          console.error('Error adding book to collection:', error);
          
          if (error.response?.data?.error) {
            this.showToast('error', error.response.data.error);
          } else {
            this.showToast('error', 'Gagal menyimpan buku ke koleksi');
          }
        } finally {
          this.isAdding = false;
          this.activeCollectionId = null;
        }
      },
      
      async removeFromCollection(collectionId, bookmarkId) {
        try {
          const token = localStorage.getItem('token');
          
          await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/api/collections/${collectionId}/remove-book/${bookmarkId}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Remove from bookCollectionStatus
          this.bookCollectionStatus = this.bookCollectionStatus.filter(
            status => !(status.collectionId === collectionId && status.bookmarkId === bookmarkId)
          );
          
          this.showToast('success', 'Buku berhasil dihapus dari koleksi');
        } catch (error) {
          console.error('Error removing book from collection:', error);
          this.showToast('error', 'Gagal menghapus buku dari koleksi');
        }
      },
      
      createFirstCollection() {
        this.showNewCollectionForm = true;
      },
      
      async createCollection() {
        if (!this.newCollection.name.trim()) {
          this.showToast('error', 'Nama koleksi tidak boleh kosong');
          return;
        }
        
        this.isCreating = true;
        
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/api/collections/`,
            this.newCollection,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // Add the new collection to the collections array
          this.collections.push(response.data);
          
          // Reset form and close it
          this.newCollection = {
            name: '',
            description: '',
            is_public: false
          };
          this.showNewCollectionForm = false;
          
          // Automatically add the book to this new collection
          await this.addToCollection(response.data.id);
          
          this.showToast('success', 'Koleksi baru berhasil dibuat');
        } catch (error) {
          console.error('Error creating collection:', error);
          
          if (error.response?.data?.name) {
            this.showToast('error', `Error: ${error.response.data.name[0]}`);
          } else {
            this.showToast('error', 'Gagal membuat koleksi');
          }
        } finally {
          this.isCreating = false;
        }
      },
      
      showToast(icon, text) {
        Swal.fire({
          icon,
          text,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      }
    }
  }
  </script>
  
  <style scoped>
  .add-to-collection {
    display: inline-block;
  }
  
  .bookmark-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    padding: 8px 15px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s;
    color: #333;
  }
  
  .bookmark-btn:hover {
    background-color: #e9ecef;
  }
  
  /* Modal styling */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 8px;
    padding: 25px;
    width: 100%;
    max-width: 500px;
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .modal-header h3 {
    margin: 0;
    color: #333;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #777;
  }
  
  .close-btn:hover {
    color: #333;
  }
  
  /* Loading state */
  .modal-loading {
    text-align: center;
    padding: 30px 0;
  }
  
  .loading-icon {
    font-size: 24px;
    color: #488e71;
    margin-bottom: 10px;
  }
  
  /* No collections state */
  .no-collections {
    text-align: center;
    padding: 20px 0;
  }
  
  .no-collections p {
    margin-bottom: 15px;
    color: #666;
  }
  
  /* Collection lists */
  .collection-list {
    list-style: none;
    padding: 0;
    margin: 15px 0;
  }
  
  .collection-list li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    background-color: #f8f9fa;
    border-radius: 5px;
    margin-bottom: 8px;
  }
  
  .collection-name {
    font-weight: 500;
    color: #333;
  }
  
  .add-btn, 
  .remove-btn {
    background: none;
    border: none;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .add-btn {
    color: #488e71;
  }
  
  .add-btn:hover {
    background-color: rgba(72, 142, 113, 0.1);
  }
  
  .add-btn:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
  
  .remove-btn {
    color: #dc3545;
  }
  
  .remove-btn:hover {
    background-color: rgba(220, 53, 69, 0.1);
  }
  
  /* Already saved section */
  .already-saved {
    margin-bottom: 20px;
  }
  
  .already-saved p {
    color: #666;
    margin-bottom: 10px;
  }
  
  /* New collection form */
  .new-collection-form {
    margin-top: 20px;
    border-top: 1px solid #ddd;
    padding-top: 20px;
  }
  
  .new-collection-form h4 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #333;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    color: #555;
    font-size: 14px;
  }
  
  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
  
  .collection-actions {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }
  
  /* Buttons */
  .btn-primary,
  .btn-secondary,
  .btn-cancel {
    padding: 8px 15px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    border: none;
  }
  
  .btn-primary {
    background-color: #488e71;
    color: white;
  }
  
  .btn-primary:hover:not(:disabled) {
    background-color: #3a7259;
  }
  
  .btn-primary:disabled {
    background-color: #93c3b1;
    cursor: not-allowed;
  }
  
  .btn-secondary {
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
  }
  
  .btn-secondary:hover {
    background-color: #e9ecef;
  }
  
  .btn-cancel {
    background-color: #f8f9fa;
    color: #777;
  }
  
  .btn-cancel:hover {
    background-color: #e9ecef;
  }
  
  /* Responsive styles */
  @media (max-width: 768px) {
    .modal-content {
      width: 90%;
      padding: 15px;
      margin: 15px;
    }
    
    .form-actions {
      flex-direction: column;
    }
    
    .form-actions button {
      width: 100%;
    }
  }
  </style>